import { Button, Chip } from "@heroui/react";
import { useMemo } from "react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { ModerationAction } from "@/src/components/ModerationAction";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { relativeTime } from "@/src/libs/relativeTime";
import type { ModerationComment } from "@/src/pages/Posts/posts.type";

type PostCommentsProps = {
  comments: ModerationComment[];
  total: number;
  page: number;
  pageCount: number;
  pendingCommentId: number | undefined;
  onDelete: (_commentId: number) => void;
  onPageChange: (_page: number) => void;
};

/** Un commentaire racine, avec ses réponses directes. */
type Thread = ModerationComment & { replies: ModerationComment[] };

/**
 * Le fil de commentaires d'une publication.
 *
 * ## L'imbrication se fait ici, pas au serveur
 *
 * La route rend une liste **plate** : paginer un arbre ferait manquer une
 * réponse dès que sa racine tombe sur la page suivante. L'écran regroupe donc
 * lui-même sur `parentCommentId` — et une réponse dont la racine n'est pas dans
 * la page **remonte au premier niveau** plutôt que de disparaître : sur une file
 * de modération, un commentaire manquant se lit comme une preuve escamotée.
 *
 * ## Un commentaire supprimé garde son texte
 *
 * ⚠️ Contrairement à ce que lit l'application, où il devient « Commentaire
 * supprimé ». La suppression est le plus souvent la suite donnée à un
 * signalement : la masquer effacerait la preuve. D'où le **barré**, qui dit les
 * deux choses à la fois — ce qui a été écrit, et qu'il ne s'affiche plus.
 */
export function PostComments({
  comments,
  total,
  page,
  pageCount,
  pendingCommentId,
  onDelete,
  onPageChange,
}: PostCommentsProps) {
  const t = useTranslations("posts");

  const threads = useMemo(() => regrouper(comments), [comments]);

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-3">
        {threads.map((thread) => (
          <li className="flex flex-col gap-2" key={thread.id}>
            <CommentLine
              comment={thread}
              isPending={pendingCommentId === thread.id}
              onDelete={onDelete}
            />

            {thread.replies.length > 0 ? (
              // Un seul niveau, comme le serveur : au quatrième niveau
              // d'indentation il ne resterait qu'une colonne de six caractères.
              <ul className="flex flex-col gap-2 border-l border-separator pl-4">
                {thread.replies.map((reply) => (
                  <li key={reply.id}>
                    <CommentLine
                      comment={reply}
                      isPending={pendingCommentId === reply.id}
                      onDelete={onDelete}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>

      {pageCount > 1 ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            isDisabled={page <= 1}
            size="sm"
            variant="ghost"
            onPress={() => onPageChange(page - 1)}
          >
            {t("previous")}
          </Button>

          <span className="text-xs text-muted tabular-nums">
            {t("page-of", { page, pages: pageCount })}
          </span>

          <Button
            isDisabled={page >= pageCount}
            size="sm"
            variant="ghost"
            onPress={() => onPageChange(page + 1)}
          >
            {t("next")}
          </Button>

          <span className="text-xs text-muted">
            {t("comments-total", { count: total })}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function CommentLine({
  comment,
  isPending,
  onDelete,
}: {
  comment: ModerationComment;
  isPending: boolean;
  onDelete: (_commentId: number) => void;
}) {
  const t = useTranslations("posts");
  const { locale } = useLocale();

  const age = relativeTime(comment.createdAt, locale, Date.now());
  const auteur = comment.author?.username ?? "";

  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1 rounded-lg bg-default p-3 text-default-foreground">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
          <AccountBadge account={comment.author} isLinked />

          {age ? <span>{age}</span> : null}

          {/* Un commentaire signalé ne se juge pas comme un commentaire
              ordinaire : le nombre est ce qui décide de l'ouvrir. */}
          {comment.reportCount > 0 ? (
            <Chip color="danger" size="sm" variant="soft">
              {t("reports-count", { count: comment.reportCount })}
            </Chip>
          ) : null}

          {comment.isDeleted ? (
            <Chip color="default" size="sm" variant="secondary">
              {t("comment-deleted")}
            </Chip>
          ) : null}

          {comment.likeCount > 0 ? (
            <span className="tabular-nums">
              {t("likes-count", { count: comment.likeCount })}
            </span>
          ) : null}
        </div>

        {/* ⚠️ Barré, et non masqué : le serveur conserve le texte d'un
            commentaire supprimé précisément pour qu'il reste lisible ici. */}
        <p
          className={`text-sm text-pretty whitespace-pre-wrap ${
            comment.isDeleted ? "text-muted line-through" : ""
          }`}
        >
          {comment.content}
        </p>
      </div>

      {/*
        ⚠️ **Effacer un commentaire est définitif**, et emporte ses réponses par
        cascade — ce n'est pas la suppression douce de son auteur, qui laisse la
        ligne lisible ici. Le geste reste offert sur un commentaire déjà supprimé
        par son auteur : la suppression douce ne retire rien de la base, c'est
        celle-ci qui efface.
      */}
      <ModerationAction
        confirmBody={t("confirm-delete-comment-body", { author: auteur })}
        confirmLabel={t("delete-comment")}
        confirmTitle={t("confirm-delete-comment-title")}
        isPending={isPending}
        label={t("delete-comment")}
        onConfirm={() => onDelete(comment.id)}
        pendingLabel={t("deleting")}
        tone="danger"
      />
    </div>
  );
}

/**
 * Regroupe la liste plate en fils d'un niveau.
 *
 * ⚠️ **Une réponse orpheline reste affichée**, au premier niveau : sa racine
 * peut être sur une autre page. La faire disparaître ferait mentir le compte des
 * commentaires, et sur une file de modération c'est exactement le genre
 * d'escamotage qu'on ne peut pas se permettre.
 */
function regrouper(comments: ModerationComment[]): Thread[] {
  const racines = new Map<number, Thread>();
  const fils: Thread[] = [];

  for (const comment of comments) {
    if (comment.parentCommentId === null) {
      const thread = { ...comment, replies: [] };
      racines.set(comment.id, thread);
      fils.push(thread);
    }
  }

  for (const comment of comments) {
    if (comment.parentCommentId === null) continue;

    const racine = racines.get(comment.parentCommentId);

    if (racine) racine.replies.push(comment);
    else fils.push({ ...comment, replies: [] });
  }

  return fils;
}
